// @flow
import * as React from 'react';
import OpticalPortSplicedFiberRow
    from 'esm-web-pro/containers/pages/network/objects/point12/poplayer12/OpticalPortSplicedFiberRow';
import OpticalPortSplicedPortRow from
    'esm-web-pro/containers/pages/network/objects/point12/poplayer12/OpticalPortSplicedPortRow';
import { Box } from 'grommet';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import BaseEditor from 'esm-web-pro/containers/map/editor/BaseEditor';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import type { BreakConnectionPayload } from 'esm-web-pro/api/resources/OpticalODFPortResource';
import { Confirmation, connectAPI, withConfirmation } from 'speed-js-react';
import sidePanelMessages from 'esm-web-pro/messages/sidePanelMessages';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import ReactTooltip from 'react-tooltip';
import { POINT12_CONTENT_TOOLTIP_KEY } from
    'esm-web-pro/containers/pages/network/objects/point12/CableList/Point12CableList';
import LabelText from 'esm-web-pro/components/layout/sidePanel/LabelText';

type Props = {
    opticalPort: Object,
    map: Object,
    editor: BaseEditor,
    confirm: (confirmation: Confirmation) => void,
    intl: IntlShape,
    breakConnection: (id: string, data: BreakConnectionPayload) => Promise<Object>,
    updateObject: Function,
    pointCoords: [number, number],
}

const OpticalPortContent = (props: Props) => {
    const {
        opticalPort: {
            fiber, port, uuid: opticalPortId, type,
        },
        opticalPort, map, editor, confirm, intl: { formatMessage }, breakConnection, updateObject,
        pointCoords,
    } = props;

    const [clickedRowIdentity, setClickedRowIdentity] = React.useState('');
    const parentRowId = `OOP${opticalPort.id}`;

    const getFiberCableSeq = (cable) => {
        const {
            start_termination_point: cableStartPoint,
            start_sequence: startSequence, end_sequence: endSequence,
            patch, patch_sequence: patchSequence,
        } = cable;

        if (patch) {
            return patchSequence;
        }
        return cableStartPoint && cableStartPoint.id === opticalPort.container.id ? startSequence : endSequence;
    };

    const handleDisconnect = (data) => {
        confirm({
            message: <FormattedMessage
                id="opticalodfport-disconnect-port-confirm"
                defaultMessage="Are you sure you want to disconnect the port?"
            />,
            actions: {
                confirm: {
                    label: formatMessage(sidePanelMessages.okButton),
                    callback: () => {
                        breakConnection(opticalPortId, data).then((res) => {
                            if (res.status === HTTP_400_BAD_REQUEST) {
                                confirm({
                                    message: res.data.error,
                                    actions: {
                                        cancel: {
                                            label: formatMessage(sidePanelMessages.okButton),
                                            callback: () => {},
                                            type: 'negative',
                                        },
                                    },
                                });
                            } else if (data.fiber) {
                                updateObject({ fiber: res.fiber });
                            } else if (data.port) {
                                updateObject({ port: res.port });
                            }
                        });
                    },
                    type: 'positive',
                },
                cancel: {
                    label: formatMessage(sidePanelMessages.cancelButton),
                    type: 'negative',
                    callback: () => {},
                },
            },
        });
    };

    return (
        <Box pad={{ vertical: '20px', horizontal: 'small' }} height={{ min: '100%' }}>
            <Box pad={{ horizontal: 'small' }}>
                <LabelText>
                    <FormattedMessage id="opticalodfport-content-title" defaultMessage="Fibers" />
                </LabelText>
            </Box>
            <Box>
                {(type !== 'opticalswitchport') && (
                    <OpticalPortSplicedFiberRow
                        clickedRowIdentity={clickedRowIdentity}
                        setClickedRowIdentity={setClickedRowIdentity}
                        getFiberCableSeq={getFiberCableSeq}
                        fiber={fiber}
                        parentRowId={parentRowId}
                        pad={{ top: 'xxsmall', bottom: 'xxsmall' }}
                        allowSplicing={false}
                        map={map}
                        editor={editor}
                        onDisconnect={() => handleDisconnect({ fiber: fiber.id })}
                        pointCoords={pointCoords}
                    />
                )}
                <OpticalPortSplicedPortRow
                    clickedRowIdentity={clickedRowIdentity}
                    setClickedRowIdentity={setClickedRowIdentity}
                    port={port}
                    parentRowId={parentRowId}
                    pad={{ top: 'xxsmall', bottom: 'xxsmall' }}
                    allowSplicing={false}
                    onDisconnect={() => handleDisconnect({ port: port.id })}
                    map={map}
                />
                <ReactTooltip
                    id={POINT12_CONTENT_TOOLTIP_KEY}
                    effect="solid"
                    place="top"
                    className="react-tooltip"
                />
            </Box>
        </Box>
    );
};


export const apiCallbacks = (apiClient: ApiClient, props: Props) => ({
    breakConnection: {
        send: (id: string, data: BreakConnectionPayload) => apiClient.resources[props.opticalPort.port.type]
            .breakConnection(id, data),
    },
});

export default injectIntl(withConfirmation(connectAPI(apiCallbacks)(OpticalPortContent)));
